import { FieldErrors } from 'react-hook-form';

import { z } from 'zod';

import { TrackingUrlArray, UploadedImageFile } from '@anyx/feature-crm/shared';


export { UploadedImageFile };

export const LineMessageBlockType = z.enum([
  'BUTTON',
  'IMAGES',
  'IMAGE_VIDEO',
  'PLAIN_TEXT',
  'RICH',
]);
export type LineMessageBlockType = z.infer<typeof LineMessageBlockType>;
export const LineMessageBlockTypeWithImageUpload = LineMessageBlockType.extract([
  'BUTTON',
  'IMAGES',
  'RICH',
]);
export type LineMessageBlockTypeWithImageUpload = z.infer<
  typeof LineMessageBlockTypeWithImageUpload
>;

export const FontSizeEnum = z.enum(['lg', 'md', 'sm', 'xl', 'xxl']);
export type FontSizeEnum = z.infer<typeof FontSizeEnum>;
export const AlignEnum = z.enum(['start', 'center', 'end']);
export type AlignEnum = z.infer<typeof AlignEnum>;

const PlainTextBlock = z.object({
  type: z.literal(LineMessageBlockType.enum.PLAIN_TEXT),
  text: z.string(),
  richText: z.string().optional(),
});

export type PlainTextBlock = z.infer<typeof PlainTextBlock>;

export const StyledTextInputComponent = z.object({
  text: z.string(),
  richText: z.string().optional(),
  weighted: z.boolean(),
  fontSize: FontSizeEnum,
  align: AlignEnum,
});

export type StyledTextInputComponent = z.infer<typeof StyledTextInputComponent>;

export const PostbackAction = z.enum(['URL_MANUAL', 'POSTBACK', 'URL_VARIABLE', 'CLIPBOARD']);
export type PostbackAction = z.infer<typeof PostbackAction>;

export const UrlActionType = z.enum(['MANUAL', 'VARIABLE']);
export type UrlActionType = z.infer<typeof UrlActionType>;

export const ManualUrlAction = z.object({
  status: z.literal(UrlActionType.enum.MANUAL),
  manual: z.string(),
});
export type ManualUrlAction = z.infer<typeof ManualUrlAction>;

export const VariableUrlAction = z.object({
  status: z.literal(UrlActionType.enum.VARIABLE),
  variable: z.string(),
});
export type VariableUrlAction = z.infer<typeof VariableUrlAction>;

export const UrlAction = z.discriminatedUnion('status', [ManualUrlAction, VariableUrlAction]);
export type UrlAction = z.infer<typeof UrlAction>;

export type PlainTextBlockFieldErrors = FieldErrors<PlainTextBlock> | undefined;

const RichMessageBlock = z.object({
  type: z.literal(LineMessageBlockType.enum.RICH),
  image: UploadedImageFile.optional(),
  actionUrl: UrlAction,
});

export type RichMessageBlock = z.infer<typeof RichMessageBlock>;

export type RichMessageBlockFieldErrors = FieldErrors<RichMessageBlock> | undefined;

const ImageBlockCards = z.object({
  image: UploadedImageFile.optional(),
  actionLabel: z.string().optional(),
  actionUrl: UrlAction,
});

const ImageTypeBlock = z.object({
  type: z.literal(LineMessageBlockType.enum.IMAGES),
  cards: z.array(ImageBlockCards),
});

export type ImageTypeBlock = z.infer<typeof ImageTypeBlock>;

export type ImageTypeBlockFieldErrors = FieldErrors<ImageTypeBlock> | undefined;

export const ButtonTypeAction = z.object({
  messageId: z.string().optional(),
  postbackAction: PostbackAction,
  urlVariable: z.string(),
  label: z.string(),
  url: z.string(),
});
export type ButtonTypeAction = z.infer<typeof ButtonTypeAction>;

const ButtonTypeCard = z.object({
  image: UploadedImageFile.optional(),
  title: StyledTextInputComponent,
  description: StyledTextInputComponent,
  actions: z.array(ButtonTypeAction),
  isImageBelowDescription: z.boolean(),
});

export type ButtonTypeCard = z.infer<typeof ButtonTypeCard>;

const ButtonTypeBlock = z.object({
  type: z.literal(LineMessageBlockType.enum.BUTTON),
  cards: z.array(ButtonTypeCard),
});

export type ButtonTypeBlock = z.infer<typeof ButtonTypeBlock>;
export type ButtonTypeBlockFieldErrors = FieldErrors<ButtonTypeBlock> | undefined;

const ImageVideoBlock = z.object({
  type: z.literal(LineMessageBlockType.enum.IMAGE_VIDEO),
});

export type ImageVideoBlock = z.infer<typeof ImageVideoBlock>;

export type ImageVideoBlockFieldErrors = FieldErrors<ImageVideoBlock> | undefined;

const SingleImageBlock = z.object({
  type: z.literal('SINGLE_IMAGE'),
  originalUrl: z.string(),
  previewUrl: z.string(),
});

export type SingleImageBlock = z.infer<typeof SingleImageBlock>;

const LineMessageBlock = z.union([
  PlainTextBlock,
  RichMessageBlock,
  ImageTypeBlock,
  ButtonTypeBlock,
  ImageVideoBlock,
]);

export type LineMessageBlock = z.infer<typeof LineMessageBlock>;

// Extended union for display purposes (includes receive-only message types)
export type LineDisplayMessageBlock = LineMessageBlock | SingleImageBlock;

export const LineMessageFormFieldsProps = z.object({
  trackingEnabled: z.boolean().optional(),
  urlLinkGroupId: z.string().optional(),
  trackingUrls: TrackingUrlArray.optional(),
  messages: z.array(LineMessageBlock),
});

export type LineMessageFormFieldsProps = z.infer<typeof LineMessageFormFieldsProps>;
